/**
 * PageLoader — visual fullscreen loader shared by:
 *  - LoadingScreen (post-login transition, auto-dismisses)
 *  - RequireAuth / RootRedirect guards (shows while auth status resolves)
 *
 * Renders the same background as the auth pages (blobs + ERD clusters)
 * with the logo and the api → front animated connector.
 */
import { useSettingsStore } from '@/stores/settingsStore'
import logoNameDark from '@/assets/logo_name_dark.png'
import logoNameLight from '@/assets/logo_name_light.png'

export function PageLoader() {
  const resolvedTheme = useSettingsStore((s) => s.resolvedTheme)
  const d = resolvedTheme === 'dark'

  const vi = (a: number) => d ? `rgba(99,102,241,${a})` : `rgba(67,56,202,${Math.min(a * 3.2, 0.98)})`
  const vc = (a: number) => d ? `rgba(6,182,212,${a})`  : `rgba(8,145,178,${Math.min(a * 3.2, 0.98)})`
  const vp = (a: number) => d ? `rgba(124,58,237,${a})` : `rgba(109,40,217,${Math.min(a * 3.2, 0.98)})`
  const it = d ? 'rgba(165,180,252,0.92)' : 'rgba(49,46,129,0.96)'
  const ct = d ? 'rgba(103,232,249,0.92)' : 'rgba(14,116,144,0.96)'

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      overflow: 'hidden',
      background: d
        ? '#020617'
        : 'linear-gradient(135deg, #C3CEE8 0%, #D0DAEA 35%, #C8D6EE 65%, #C0CCEC 100%)',
    }}>

      {/* ── Decorative background (blurred) ── */}
      <div style={{
        position: 'absolute',
        inset: 0,
        filter: 'blur(6px)',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}>
        {/* Blobs */}
        <div className="auth-blob-1" style={{
          position: 'absolute', top: '-18%', right: '-8%',
          width: '62vw', height: '62vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(124,58,237,0.26) 0%, rgba(37,99,235,0.15) 40%, transparent 70%)'
            : 'radial-gradient(circle, rgba(124,58,237,0.42) 0%, rgba(37,99,235,0.24) 45%, transparent 72%)',
          filter: 'blur(64px)',
        }} />
        <div className="auth-blob-2" style={{
          position: 'absolute', bottom: '-22%', left: '-12%',
          width: '68vw', height: '68vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(6,182,212,0.22) 0%, rgba(37,99,235,0.11) 40%, transparent 70%)'
            : 'radial-gradient(circle, rgba(6,182,212,0.38) 0%, rgba(37,99,235,0.20) 45%, transparent 72%)',
          filter: 'blur(80px)',
        }} />
        <div className="auth-blob-3" style={{
          position: 'absolute', top: '-5%', left: '-15%',
          width: '40vw', height: '40vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(37,99,235,0.16) 0%, transparent 65%)'
            : 'radial-gradient(circle, rgba(37,99,235,0.30) 0%, transparent 65%)',
          filter: 'blur(50px)',
        }} />
        <div className="auth-center-glow" style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '50vw', height: '50vw', borderRadius: '50%',
          background: d
            ? 'radial-gradient(circle, rgba(99,102,241,0.13) 0%, transparent 62%)'
            : 'radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 62%)',
          filter: 'blur(40px)',
        }} />

        {/* Dot grid */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          <defs>
            <pattern id="pl-dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="0" cy="0" r="0.9" fill={d ? 'rgba(148,163,184,0.14)' : 'rgba(55,65,120,0.2)'} />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#pl-dots)" />
        </svg>

        {/* Vignette */}
        <div style={{
          position: 'absolute', inset: 0,
          background: d
            ? 'radial-gradient(ellipse at center, transparent 38%, rgba(2,6,23,0.55) 100%)'
            : 'radial-gradient(ellipse at center, transparent 40%, rgba(148,163,210,0.35) 100%)',
        }} />

        {/* ERD ghost — top-left */}
        <svg
          style={{ position: 'absolute', top: 0, left: 0, width: '46%', height: '55%', opacity: d ? 0.32 : 0.42 }}
          viewBox="0 0 570 460" preserveAspectRatio="xMinYMin meet" overflow="visible"
        >
          <line x1="100" y1="80" x2="175" y2="80" stroke={vi(0.32)} strokeWidth="1" strokeDasharray="5,4" className="auth-line-flow-1" />
          <line x1="265" y1="80" x2="325" y2="80" stroke={vi(0.22)} strokeWidth="1" strokeDasharray="4,5" className="auth-line-flow-2" />
          <line x1="220" y1="106" x2="220" y2="185" stroke={vc(0.28)} strokeWidth="1" />
          <rect x="8"   y="62" width="92" height="16" rx="3" fill={vi(0.22)} stroke={vi(0.38)} strokeWidth="1" />
          <text x="16"  y="73.5" fontSize="7.5" fill={it} fontFamily="monospace">users</text>
          <rect x="8"   y="78" width="92" height="10" fill={vi(0.07)} stroke={vi(0.14)} strokeWidth="0.5" />
          <rect x="12"  y="80.5" width="28" height="5" rx="1" fill={vi(0.22)} />
          <rect x="175" y="62" width="90" height="16" rx="3" fill={vc(0.2)} stroke={vc(0.32)} strokeWidth="1" />
          <text x="183" y="73.5" fontSize="7.5" fill={ct} fontFamily="monospace">diagrams</text>
          <rect x="175" y="78" width="90" height="10" fill={vc(0.06)} stroke={vc(0.12)} strokeWidth="0.5" />
          <rect x="179" y="80.5" width="35" height="5" rx="1" fill={vc(0.18)} />
          <rect x="325" y="62" width="90" height="16" rx="3" fill={vp(0.18)} stroke={vp(0.3)} strokeWidth="1" />
          <text x="333" y="73.5" fontSize="7.5" fill={it} fontFamily="monospace">permissions</text>
          <rect x="175" y="185" width="90" height="16" rx="3" fill={vc(0.19)} stroke={vc(0.3)} strokeWidth="1" />
          <text x="183" y="196.5" fontSize="7.5" fill={ct} fontFamily="monospace">projects</text>
        </svg>

        {/* ERD ghost — bottom-right */}
        <svg
          style={{ position: 'absolute', bottom: 0, right: 0, width: '46%', height: '55%', opacity: d ? 0.28 : 0.38 }}
          viewBox="0 0 570 460" preserveAspectRatio="xMaxYMax meet" overflow="visible"
        >
          <line x1="350" y1="290" x2="350" y2="178" stroke={vi(0.26)} strokeWidth="1" />
          <line x1="255" y1="275" x2="160" y2="275" stroke={vi(0.28)} strokeWidth="1" strokeDasharray="5,4" className="auth-line-flow-2" />
          <line x1="115" y1="259" x2="115" y2="185" stroke={vc(0.24)} strokeWidth="1" />
          <rect x="255" y="257" width="92" height="16" rx="3" fill={vi(0.22)} stroke={vi(0.36)} strokeWidth="1" />
          <text x="263" y="268.5" fontSize="7.5" fill={it} fontFamily="monospace">tables</text>
          <rect x="305" y="162" width="90" height="16" rx="3" fill={vi(0.17)} stroke={vi(0.28)} strokeWidth="1" />
          <text x="313" y="173.5" fontSize="7.5" fill={it} fontFamily="monospace">indexes</text>
          <rect x="155" y="157" width="90" height="16" rx="3" fill={vc(0.2)} stroke={vc(0.32)} strokeWidth="1" />
          <text x="163" y="168.5" fontSize="7.5" fill={ct} fontFamily="monospace">columns</text>
          <rect x="62"  y="257" width="93" height="16" rx="3" fill={vc(0.17)} stroke={vc(0.28)} strokeWidth="1" />
          <text x="70"  y="268.5" fontSize="7.5" fill={ct} fontFamily="monospace">relations</text>
        </svg>

        {/* Pulse rings */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="auth-ring-1" style={{
            position: 'absolute', width: 520, height: 520, borderRadius: '50%',
            border: d ? '1px solid rgba(99,102,241,0.18)' : '1px solid rgba(99,102,241,0.28)',
          }} />
          <div className="auth-ring-2" style={{
            position: 'absolute', width: 680, height: 680, borderRadius: '50%',
            border: d ? '1px solid rgba(6,182,212,0.12)' : '1px solid rgba(6,182,212,0.22)',
          }} />
        </div>
      </div>

      {/* ── Center content (sharp) ── */}
      <div style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 44,
      }}>

        {/* Logo */}
        <img
          src={d ? logoNameDark : logoNameLight}
          alt="MorphERD"
          style={{
            height: 38,
            width: 'auto',
            filter: d
              ? 'drop-shadow(0 0 20px rgba(99,102,241,0.5))'
              : 'drop-shadow(0 0 14px rgba(67,56,202,0.35))',
          }}
        />

        {/* ── api → front animation ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <svg
            viewBox="0 0 500 96"
            width="500"
            height="96"
            style={{ overflow: 'visible', maxWidth: 'min(500px, 90vw)' }}
          >
            <defs>
              <filter id="pl-glow" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="3.5" />
              </filter>
              <marker id="pl-arrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
                <path d="M0,1 L0,6 L7,3.5 z" fill={vc(0.9)} />
              </marker>
            </defs>

            {/* api table */}
            <rect x="8"   y="8"  width="138" height="20" rx="4" fill={vi(0.28)} stroke={vi(0.6)} strokeWidth="1.5" />
            <text x="20"  y="22" fontSize="12" fill={it} fontFamily="monospace" fontWeight="bold">api</text>
            <rect x="8"   y="28" width="138" height="14" fill={vi(0.10)} stroke={vi(0.22)} strokeWidth="0.8" />
            <rect x="14"  y="32" width="52"  height="6"  rx="2" fill={vi(0.32)} />
            <rect x="8"   y="42" width="138" height="14" fill={vi(0.07)} stroke={vi(0.15)} strokeWidth="0.8" />
            <rect x="14"  y="46" width="72"  height="6"  rx="2" fill={vi(0.20)} />
            <rect x="8"   y="56" width="138" height="14" fill={vi(0.07)} stroke={vi(0.15)} strokeWidth="0.8" />
            <rect x="14"  y="60" width="60"  height="6"  rx="2" fill={vi(0.20)} />
            <rect x="8"   y="70" width="138" height="14" fill={vi(0.05)} stroke={vi(0.10)} strokeWidth="0.8" />
            <rect x="14"  y="74" width="42"  height="6"  rx="2" fill={vi(0.14)} />

            {/* front table */}
            <rect x="354" y="8"  width="138" height="20" rx="4" fill={vc(0.28)} stroke={vc(0.6)} strokeWidth="1.5" />
            <text x="366" y="22" fontSize="12" fill={ct} fontFamily="monospace" fontWeight="bold">front</text>
            <rect x="354" y="28" width="138" height="14" fill={vc(0.10)} stroke={vc(0.22)} strokeWidth="0.8" />
            <rect x="360" y="32" width="56"  height="6"  rx="2" fill={vc(0.32)} />
            <rect x="354" y="42" width="138" height="14" fill={vc(0.07)} stroke={vc(0.15)} strokeWidth="0.8" />
            <rect x="360" y="46" width="76"  height="6"  rx="2" fill={vc(0.20)} />
            <rect x="354" y="56" width="138" height="14" fill={vc(0.07)} stroke={vc(0.15)} strokeWidth="0.8" />
            <rect x="360" y="60" width="48"  height="6"  rx="2" fill={vc(0.20)} />
            <rect x="354" y="70" width="138" height="14" fill={vc(0.05)} stroke={vc(0.10)} strokeWidth="0.8" />
            <rect x="360" y="74" width="64"  height="6"  rx="2" fill={vc(0.14)} />

            {/* connector glow */}
            <line x1="146" y1="46" x2="354" y2="46"
              stroke={vc(0.4)} strokeWidth="6"
              filter="url(#pl-glow)"
              className="auth-solid-pulse-1"
            />
            {/* dashed line */}
            <line
              x1="146" y1="46" x2="348" y2="46"
              stroke={vc(0.7)} strokeWidth="1.8"
              strokeDasharray="7,5"
              className="auth-line-flow-1"
              markerEnd="url(#pl-arrow)"
            />

            {/* anchor nodes */}
            <circle cx="146" cy="46" r="5" fill={vi(0.9)}
              style={{ filter: `drop-shadow(0 0 5px ${vi(0.7)})` }} />
            <circle cx="354" cy="46" r="5" fill={vc(0.9)}
              style={{ filter: `drop-shadow(0 0 5px ${vc(0.7)})` }} />

            {/* traveling dots */}
            <circle r="4" fill={vc(1)}>
              <animateMotion dur="1.4s" repeatCount="indefinite" path="M146,46 L354,46" />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.05;0.88;1" dur="1.4s" repeatCount="indefinite" />
            </circle>
            <circle r="4" fill={vc(0.9)}>
              <animateMotion dur="1.4s" repeatCount="indefinite" begin="0.7s" path="M146,46 L354,46" />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.05;0.88;1" dur="1.4s" repeatCount="indefinite" begin="0.7s" />
            </circle>
            <circle r="2.5" fill={vp(0.95)}>
              <animateMotion dur="2.1s" repeatCount="indefinite" begin="0.35s" path="M146,46 L354,46" />
              <animate attributeName="opacity" values="0;0.85;0.85;0" keyTimes="0;0.05;0.88;1" dur="2.1s" repeatCount="indefinite" begin="0.35s" />
            </circle>
          </svg>

          <span style={{
            fontSize: 12,
            letterSpacing: '0.12em',
            fontFamily: 'monospace',
            textTransform: 'uppercase',
            color: d ? 'rgba(148,163,184,0.7)' : 'rgba(71,85,105,0.75)',
          }}>
            carregando
          </span>
        </div>
      </div>
    </div>
  )
}
